<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useConfigStore } from '@/stores/config'
import { useTokenStore } from '@/stores/token'
import { useLanguageStore } from '@/stores/language'
import { useTranslaterStore } from '@/stores/translater'
import { useWsStore } from '@/stores/ws'
import { useRequestsStore } from '@/stores/requests'

import Modal from '@/components/Modal.vue'
import ASWBreadcrumbs from '@/components/ASWBreadcrumbs.vue'
import ASWHeader from '@/components/ASWHeader.vue'
import ASWFooter from '@/components/ASWFooter.vue'
import ASWWsIndicator from '@/components/ASWWsIndicator.vue'

const route = useRoute()

// Stores
const tokenStore = useTokenStore()
const configStore = useConfigStore()
const languageStore = useLanguageStore()
const translaterStore = useTranslaterStore()
const wsStore = useWsStore()
const requestsStore = useRequestsStore()

// Data
const BASE_URL = window.location.host
const BASE_PROTOCOL = window.location.protocol

const showNeedReauth = ref(false)

// Computed
const token = computed(() => tokenStore.access)
const API_version = computed(() => configStore.API_version)
const lang = computed(() => languageStore.lang)
const wsNeedReauth = computed(() => wsStore.wsNeedReauth)
const getTranslate = computed(() => translaterStore.getTranslate)
const wsReady = computed(() => wsStore.wsReady)

// Определение экрана мобильных устройств
function identifyDevice() {
    document.getElementsByTagName('body')[0].clientWidth < 576
        ? (configStore.isMobile = true)
        : (configStore.isMobile = false)
}

// Определяем с какими серверами API будем работать [dev или test/production]
if (import.meta.env.DEV) {
    configStore.httpServer = `/${API_version.value}`
    configStore.wsServer = `ws://${window.location.host}/ws`
} else {
    configStore.httpServer = BASE_PROTOCOL + '//' + BASE_URL + '/' + API_version.value
    configStore.wsServer = (BASE_PROTOCOL == 'http:' ? 'ws' : 'wss') + '://' + BASE_URL + '/' + API_version.value
}

// Устанавливаем язык для интерфейса приложения
languageStore.updateLanguage(localStorage.getItem('ASW_LANG') || 'RU')

// Зачем то до сих пор ставим индикатор языка в HTML
document.getElementsByTagName('html')[0].setAttribute('lang', lang.value.toLowerCase())

// Добавляем токен для авторизованных запросов
tokenStore.addToken().then(token => {
    if (token) {
        wsStore.openWs()
    }
})

// Определяем платформу браузера для особенностей работы с Linux-системами
// const isLinux = navigator?.userAgentData?.platform == 'Linux'

// Параметры zoom control для Leaflet на линуксах работают немножко по е####тому, исправим это
// configStore.leafletConfig.zoom = {
//     snap: isLinux ? 0.1 : 1,
//     wheel: isLinux ? 200 : 600
// }

// @TODO: Можно удалить после 10.08.2026
localStorage.removeItem('ASW_ECUS_VIEW_MODE')

// @TODO: Можно удалить после 16.09.2026
localStorage.removeItem('ASW_YMAP_LAYERS')

// Lifecycle Hooks
onMounted(() => {
    window.addEventListener('resize', identifyDevice)

    // Определить мобильное устройство
    identifyDevice()
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', identifyDevice)
})

// Watchers
watch(wsNeedReauth, newVal => {
    if (newVal) showNeedReauth.value = true
})
</script>

<template>
    <ASWHeader v-if="token" />

    <div class="asw-main">
        <ASWBreadcrumbs v-if="route.name !== 'auth' && route.name !== 'forgot' && route.name !== 'registration'" />
        <router-view />
        <ASWWsIndicator v-if="!wsReady && token" />
    </div>

    <ASWFooter v-if="token" />

    <transition name="bounce">
        <Modal
            v-if="showNeedReauth"
            :header="getTranslate.ERROR_NEED_REAUTH_TITLE"
            size="lgf"
            @closeModal="requestsStore.logout"
        >
            <template v-slot:body>
                {{ getTranslate.ERROR_NEED_REAUTH_TEXT }}
                <div class="mt-4 btns">
                    <a class="btn btn--primary" href="#" @click.prevent="requestsStore.logout">{{
                        getTranslate.ERROR_NEED_REAUTH_BTN
                    }}</a>
                </div>
            </template>
        </Modal>
    </transition>
</template>

<style lang="scss">
@use '@/assets/styles/scss/components/modal.scss';
</style>
